import React, { useState } from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Badge } from '../../components/ui/Badge';
import { Settings, User, Shield, Activity, Search, Plus, Lock, Edit, Trash2, RefreshCw, Download, CheckCircle, XCircle, Eye, FileText, Calendar, Users, Database, AlertTriangle, Check, X, Bell, Sliders, Filter } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { ROLE_PERMISSIONS, ROLE_LABELS, type UserRole, type Permission } from '../../lib/auth';
// Expanded Mock Users
const mockUsers = [{
  id: '1',
  name: 'Admin Pusat',
  email: 'admin@klhk.go.id',
  role: 'super_admin' as UserRole,
  region: 'Pusat',
  status: 'active',
  lastLogin: 'Baru saja'
}, {
  id: '2',
  name: 'Admin DKI Jakarta',
  email: 'regional@klhk.go.id',
  role: 'regional_admin' as UserRole,
  region: 'DKI Jakarta',
  status: 'active',
  lastLogin: '2 jam yang lalu'
}, {
  id: '3',
  name: 'Budi Santoso',
  email: 'petugas@klhk.go.id',
  role: 'officer' as UserRole,
  region: 'Jakarta Selatan',
  status: 'active',
  lastLogin: '1 hari yang lalu'
}, {
  id: '4',
  name: 'Viewer Monitoring',
  email: 'viewer@klhk.go.id',
  role: 'viewer' as UserRole,
  region: 'Pusat',
  status: 'active',
  lastLogin: '30 menit yang lalu'
}, {
  id: '5',
  name: 'Siti Aminah',
  email: 'siti.aminah@klhk.go.id',
  role: 'officer' as UserRole,
  region: 'Bandung',
  status: 'inactive',
  lastLogin: '1 minggu yang lalu'
}, {
  id: '6',
  name: 'Admin Jawa Barat',
  email: 'jabar@klhk.go.id',
  role: 'regional_admin' as UserRole,
  region: 'Jawa Barat',
  status: 'active',
  lastLogin: '5 jam yang lalu'
}];
// Mock Audit Logs
const mockAuditLogs = [{
  id: '1',
  timestamp: '2025-02-04 10:30:15',
  user: 'Admin Pusat',
  action: 'LOGIN',
  target: 'System',
  ip: '192.168.1.1',
  status: 'success'
}, {
  id: '2',
  timestamp: '2025-02-04 10:35:22',
  user: 'Admin Pusat',
  action: 'CREATE_USER',
  target: 'User: Budi Santoso',
  ip: '192.168.1.1',
  status: 'success'
}, {
  id: '3',
  timestamp: '2025-02-04 11:15:00',
  user: 'Admin DKI Jakarta',
  action: 'UPDATE_CONTENT',
  target: 'Campaign: HPSN 2025',
  ip: '192.168.1.25',
  status: 'success'
}, {
  id: '4',
  timestamp: '2025-02-04 11:20:45',
  user: 'Budi Santoso',
  action: 'SUBMIT_REPORT',
  target: 'Laporan #12345',
  ip: '10.0.0.5',
  status: 'success'
}, {
  id: '5',
  timestamp: '2025-02-04 12:00:10',
  user: 'Unknown',
  action: 'LOGIN_ATTEMPT',
  target: 'System',
  ip: '203.1.1.5',
  status: 'failed'
}];
// Permission Descriptions
const permissionDescriptions: Record<keyof Permission, string> = {
  canViewDashboard: 'Melihat dashboard utama dan statistik',
  canManageUsers: 'Membuat, mengedit, dan menghapus user',
  canManageContent: 'Mengelola konten edukasi dan kampanye',
  canManageEvents: 'Membuat dan mengelola event nasional/daerah',
  canManageCommunities: 'Mengelola database komunitas dan kemitraan',
  canManageAssets: 'Mengelola registry aset fisik dan digital',
  canViewAuditLog: 'Melihat log aktivitas sistem',
  canApproveReports: 'Memvalidasi laporan dari petugas lapangan',
  canCreateReports: 'Membuat laporan isu atau kegiatan lapangan',
  canViewReports: 'Melihat daftar laporan yang masuk',
  canEditOwnRegion: 'Hanya bisa mengedit data di wilayahnya sendiri'
};
export function AdminPage() {
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  // User Table Columns
  const userColumns = [{
    header: 'Nama User',
    accessorKey: 'name' as const,
    cell: (item: any) => <div data-id="element-531">
          <div className="font-medium text-gray-900" data-id="element-532">{item.name}</div>
          <div className="text-xs text-gray-500" data-id="element-533">{item.email}</div>
        </div>
  }, {
    header: 'Role',
    accessorKey: 'role' as const,
    cell: (item: any) => <Badge variant={item.role === 'super_admin' ? 'default' : item.role === 'regional_admin' ? 'secondary' : 'outline'} data-id="element-534">
          {ROLE_LABELS[item.role]}
        </Badge>
  }, {
    header: 'Wilayah',
    accessorKey: 'region' as const
  }, {
    header: 'Status',
    cell: (item: any) => <StatusBadge status={item.status === 'active' ? 'hijau' : 'merah'} data-id="element-535">
          {item.status === 'active' ? 'Aktif' : 'Nonaktif'}
        </StatusBadge>
  }, {
    header: 'Login Terakhir',
    accessorKey: 'lastLogin' as const,
    cell: (item: any) => <span className="text-sm text-gray-500" data-id="element-536">{item.lastLogin}</span>
  }, {
    header: 'Aksi',
    cell: (item: any) => <div className="flex items-center gap-2" data-id="element-537">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => {
        setSelectedUser(item);
        setIsUserModalOpen(true);
      }} data-id="element-538">
            <Edit className="h-4 w-4" data-id="element-539" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-540">
            <Trash2 className="h-4 w-4" data-id="element-541" />
          </Button>
        </div>
  }];
  // Audit Log Columns
  const auditColumns = [{
    header: 'Waktu',
    accessorKey: 'timestamp' as const,
    cell: (item: any) => <span className="text-sm font-mono text-gray-600" data-id="element-542">
          {item.timestamp}
        </span>
  }, {
    header: 'User',
    accessorKey: 'user' as const,
    cell: (item: any) => <span className="font-medium" data-id="element-543">{item.user}</span>
  }, {
    header: 'Aksi',
    accessorKey: 'action' as const,
    cell: (item: any) => <Badge variant="outline" className="font-mono text-xs" data-id="element-544">
          {item.action}
        </Badge>
  }, {
    header: 'Target',
    accessorKey: 'target' as const
  }, {
    header: 'IP Address',
    accessorKey: 'ip' as const,
    cell: (item: any) => <span className="text-xs text-gray-500 font-mono" data-id="element-545">{item.ip}</span>
  }, {
    header: 'Status',
    accessorKey: 'status' as const,
    cell: (item: any) => <span className={`text-xs font-bold uppercase ${item.status === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-546">
          {item.status}
        </span>
  }];
  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsUserModalOpen(true);
  };
  return <CommandCenterLayout title="Admin Panel" subtitle="Manajemen user, role, dan konfigurasi sistem" data-id="element-547">
      <div className="space-y-6" data-id="element-548">
        {/* Tabs */}
        <div className="bg-white p-1 rounded-lg border inline-flex overflow-x-auto max-w-full" data-id="element-549">
          {[{
          id: 'users',
          label: 'Manajemen User',
          icon: User
        }, {
          id: 'roles',
          label: 'Role & Akses',
          icon: Lock
        }, {
          id: 'settings',
          label: 'Settings',
          icon: Sliders
        }, {
          id: 'audit',
          label: 'Audit Log',
          icon: Activity
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-primary-50 text-primary-700 shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-550">
              <tab.icon className="h-4 w-4" data-id="element-551" />
              {tab.label}
            </button>)}
        </div>

        {/* Content based on tab */}
        {activeTab === 'users' && <Card data-id="element-552">
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-4 gap-4" data-id="element-553">
              <div data-id="element-554">
                <CardTitle data-id="element-555">Daftar Pengguna</CardTitle>
                <CardDescription data-id="element-556">
                  Kelola akses pengguna aplikasi
                </CardDescription>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-557">
                <div className="relative flex-1 sm:w-64" data-id="element-558">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-559" />
                  <Input placeholder="Cari user..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-560" />
                </div>
                <Button onClick={handleCreateUser} className="bg-primary-600 text-white whitespace-nowrap" data-id="element-561">
                  <Plus className="h-4 w-4 mr-2" data-id="element-562" /> Tambah
                </Button>
              </div>
            </CardHeader>
            <CardContent data-id="element-563">
              <DataTable data={mockUsers.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()))} columns={userColumns} keyExtractor={item => item.id} data-id="element-564" />
            </CardContent>
          </Card>}

        {activeTab === 'roles' && <Card data-id="element-565">
            <CardHeader data-id="element-566">
              <CardTitle data-id="element-567">Matrix Akses & Role</CardTitle>
              <CardDescription data-id="element-568">
                Daftar hak akses untuk setiap role dalam sistem
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-569">
              <div className="overflow-x-auto" data-id="element-570">
                <table className="w-full text-sm text-left" data-id="element-571">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b" data-id="element-572">
                    <tr data-id="element-573">
                      <th className="px-6 py-4 font-bold text-gray-900 min-w-[200px]" data-id="element-574">
                        Permission / Hak Akses
                      </th>
                      {Object.keys(ROLE_LABELS).map(role => <th key={role} className="px-6 py-4 text-center min-w-[120px]" data-id="element-575">
                          <div className="flex flex-col items-center gap-1" data-id="element-576">
                            <span className="font-bold" data-id="element-577">
                              {ROLE_LABELS[role as UserRole]}
                            </span>
                            <Badge variant="outline" className="text-[10px]" data-id="element-578">
                              {role}
                            </Badge>
                          </div>
                        </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-579">
                    {Object.keys(permissionDescriptions).map(permKey => {
                  const permission = permKey as keyof Permission;
                  return <tr key={permission} className="hover:bg-gray-50" data-id="element-580">
                          <td className="px-6 py-4" data-id="element-581">
                            <div className="font-medium text-gray-900" data-id="element-582">
                              {permission}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-583">
                              {permissionDescriptions[permission]}
                            </div>
                          </td>
                          {Object.keys(ROLE_LABELS).map(role => {
                      const hasAccess = ROLE_PERMISSIONS[role as UserRole][permission];
                      return <td key={role} className="px-6 py-4 text-center" data-id="element-584">
                                {hasAccess ? <CheckCircle className="h-5 w-5 text-green-500 mx-auto" data-id="element-585" /> : <XCircle className="h-5 w-5 text-red-200 mx-auto" data-id="element-586" />}
                              </td>;
                    })}
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>}

        {activeTab === 'settings' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-587">
            <Card data-id="element-588">
              <CardHeader data-id="element-589">
                <CardTitle data-id="element-590">Konfigurasi Skoring</CardTitle>
                <CardDescription data-id="element-591">
                  Atur ambang batas (threshold) status warna daerah
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-592">
                <div className="p-4 bg-green-50 border border-green-100 rounded-lg" data-id="element-593">
                  <div className="flex justify-between mb-2" data-id="element-594">
                    <span className="font-medium text-green-800" data-id="element-595">
                      Status Hijau (Baik)
                    </span>
                    <span className="font-bold text-green-800" data-id="element-596">&gt; 75</span>
                  </div>
                  <input type="range" min="0" max="100" defaultValue="75" className="w-full accent-green-600" data-id="element-597" />
                </div>
                <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg" data-id="element-598">
                  <div className="flex justify-between mb-2" data-id="element-599">
                    <span className="font-medium text-yellow-800" data-id="element-600">
                      Status Kuning (Perlu Perhatian)
                    </span>
                    <span className="font-bold text-yellow-800" data-id="element-601">60 - 75</span>
                  </div>
                  <input type="range" min="0" max="100" defaultValue="60" className="w-full accent-yellow-600" data-id="element-602" />
                </div>
                <div className="p-4 bg-red-50 border border-red-100 rounded-lg" data-id="element-603">
                  <div className="flex justify-between mb-2" data-id="element-604">
                    <span className="font-medium text-red-800" data-id="element-605">
                      Status Merah (Bermasalah)
                    </span>
                    <span className="font-bold text-red-800" data-id="element-606">&lt; 60</span>
                  </div>
                </div>
                <Button className="w-full bg-primary-600 text-white" data-id="element-607">
                  Simpan Konfigurasi
                </Button>
              </CardContent>
            </Card>

            <Card data-id="element-608">
              <CardHeader data-id="element-609">
                <CardTitle data-id="element-610">Notifikasi System</CardTitle>
                <CardDescription data-id="element-611">
                  Pengaturan alert dan notifikasi email
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4" data-id="element-612">
                <div className="flex items-center justify-between p-3 border rounded" data-id="element-613">
                  <div className="flex items-center gap-3" data-id="element-614">
                    <Bell className="h-5 w-5 text-gray-500" data-id="element-615" />
                    <div data-id="element-616">
                      <p className="font-medium text-sm" data-id="element-617">
                        Email Alert: Isu Urgent
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-618">
                        Kirim email saat ada isu prioritas tinggi
                      </p>
                    </div>
                  </div>
                  <input type="checkbox" defaultChecked className="h-5 w-5" data-id="element-619" />
                </div>
                <div className="flex items-center justify-between p-3 border rounded" data-id="element-620">
                  <div className="flex items-center gap-3" data-id="element-621">
                    <Calendar className="h-5 w-5 text-gray-500" data-id="element-622" />
                    <div data-id="element-623">
                      <p className="font-medium text-sm" data-id="element-624">Reminder Laporan</p>
                      <p className="text-xs text-gray-500" data-id="element-625">
                        Ingatkan daerah H-1 deadline laporan
                      </p>
                    </div>
                  </div>
                  <input type="checkbox" defaultChecked className="h-5 w-5" data-id="element-626" />
                </div>
                <div className="flex items-center justify-between p-3 border rounded" data-id="element-627">
                  <div className="flex items-center gap-3" data-id="element-628">
                    <Shield className="h-5 w-5 text-gray-500" data-id="element-629" />
                    <div data-id="element-630">
                      <p className="font-medium text-sm" data-id="element-631">Security Alert</p>
                      <p className="text-xs text-gray-500" data-id="element-632">
                        Notifikasi login mencurigakan
                      </p>
                    </div>
                  </div>
                  <input type="checkbox" defaultChecked className="h-5 w-5" data-id="element-633" />
                </div>
              </CardContent>
            </Card>
          </div>}

        {activeTab === 'audit' && <Card data-id="element-634">
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-4 gap-4" data-id="element-635">
              <div data-id="element-636">
                <CardTitle data-id="element-637">Audit Log</CardTitle>
                <CardDescription data-id="element-638">
                  Riwayat aktivitas pengguna dalam sistem
                </CardDescription>
              </div>
              <div className="flex items-center gap-2" data-id="element-639">
                <Button variant="outline" className="gap-2" data-id="element-640">
                  <Calendar className="h-4 w-4" data-id="element-641" /> Filter Tanggal
                </Button>
                <Button variant="outline" className="gap-2" data-id="element-642">
                  <Filter className="h-4 w-4" data-id="element-643" /> Filter Aksi
                </Button>
                <Button variant="outline" className="gap-2" data-id="element-644">
                  <Download className="h-4 w-4" data-id="element-645" /> Export
                </Button>
              </div>
            </CardHeader>
            <CardContent data-id="element-646">
              <DataTable data={mockAuditLogs} columns={auditColumns} keyExtractor={item => item.id} data-id="element-647" />
            </CardContent>
          </Card>}
      </div>

      {/* User Modal */}
      <Modal isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)} title={selectedUser ? 'Edit User' : 'Tambah User Baru'} size="lg" data-id="element-648">
        <div className="space-y-4" data-id="element-649">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-650">
            <div className="space-y-2" data-id="element-651">
              <label className="text-sm font-medium text-gray-700" data-id="element-652">
                Nama Lengkap
              </label>
              <Input placeholder="Contoh: Budi Santoso" defaultValue={selectedUser?.name} data-id="element-653" />
            </div>
            <div className="space-y-2" data-id="element-654">
              <label className="text-sm font-medium text-gray-700" data-id="element-655">Email</label>
              <Input placeholder="email@klhk.go.id" defaultValue={selectedUser?.email} data-id="element-656" />
            </div>
            <div className="space-y-2" data-id="element-657">
              <label className="text-sm font-medium text-gray-700" data-id="element-658">Role</label>
              <Select options={[{
              value: 'super_admin',
              label: 'Super Admin'
            }, {
              value: 'regional_admin',
              label: 'Regional Admin'
            }, {
              value: 'officer',
              label: 'Petugas Lapangan'
            }, {
              value: 'viewer',
              label: 'Viewer'
            }, {
              value: 'public',
              label: 'Public'
            }]} value={selectedUser?.role || 'officer'} onChange={() => {}} data-id="element-659" />
            </div>
            <div className="space-y-2" data-id="element-660">
              <label className="text-sm font-medium text-gray-700" data-id="element-661">
                Wilayah (Region)
              </label>
              <Select options={[{
              value: 'Pusat',
              label: 'Pusat (Nasional)'
            }, {
              value: 'DKI Jakarta',
              label: 'DKI Jakarta'
            }, {
              value: 'Jawa Barat',
              label: 'Jawa Barat'
            }, {
              value: 'Jawa Tengah',
              label: 'Jawa Tengah'
            }, {
              value: 'Jawa Timur',
              label: 'Jawa Timur'
            }, {
              value: 'Jakarta Selatan',
              label: 'Jakarta Selatan'
            }]} value={selectedUser?.region || 'Pusat'} onChange={() => {}} data-id="element-662" />
            </div>
            <div className="space-y-2" data-id="element-663">
              <label className="text-sm font-medium text-gray-700" data-id="element-664">
                Status
              </label>
              <Select options={[{
              value: 'active',
              label: 'Aktif'
            }, {
              value: 'inactive',
              label: 'Nonaktif'
            }, {
              value: 'suspended',
              label: 'Suspended'
            }]} value={selectedUser?.status || 'active'} onChange={() => {}} data-id="element-665" />
            </div>
            {!selectedUser && <div className="space-y-2" data-id="element-666">
                <label className="text-sm font-medium text-gray-700" data-id="element-667">
                  Password
                </label>
                <Input type="password" placeholder="••••••••" data-id="element-668" />
              </div>}
          </div>

          <div className="pt-4 flex justify-end gap-3 border-t mt-4" data-id="element-669">
            <Button variant="outline" onClick={() => setIsUserModalOpen(false)} className="w-24" data-id="element-670">
              Batal
            </Button>
            <Button className="bg-primary-600 text-white w-24" onClick={() => setIsUserModalOpen(false)} data-id="element-671">
              Simpan
            </Button>
          </div>
        </div>
      </Modal>
    </CommandCenterLayout>;
}